import React, {
    CSSProperties,
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import { observer } from 'mobx-react';
import styled, { css } from 'styled-components';
import colors from '@honeybook/hbui/dist/styles/colors';
import is from 'styled-is';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import HBInput from '@honeybook/hbui/dist/components/HBInput/HBInput';
import spacing from '@honeybook/hbui/dist/styles/spacing';
import Pencil16 from '@honeybook/icons/dist/icons/Pencil16';
import HBTooltip from '@honeybook/hbui/dist/components/HBTooltip/HBTooltip';
import { truncatedText } from '@honeybook/hbui/dist/styles/mixins';
import { EDIT_AUTOMATION_TOOLTIP } from '../StepSideBar/StepSideBar.styles';
import { useRootStore } from '~/stores/useRootStore';
import { useTranslation } from 'react-i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import HBDropdown from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown';
import InfoCircle16 from '@honeybook/icons/dist/icons/InfoCircle16';
import HBAvatar from '@honeybook/hbui/dist/components/HBAvatar/HBAvatar';
import Check16 from '@honeybook/icons/dist/icons/Check16';
import { TeamRole } from '~/types/server.types';
import { logError } from '~/services/logger/logger.utils';
import { InputContainer } from '~/apps/main/Malkut/FlowTools/SmartFilesTools.styles';
import useAnalytics from '~/common/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';

const MAX_LENGTH = 140;

const CreateTaskInput = () => {
    const { track } = useAnalytics();
    const {
        automationUiStore: {
            tooltipCommonProps,
            isCreate,
            isShowErrors,
            isSelectedStepEditingDisabled,
            selectedAutomation,
            selectedStep: {
                taskTitle,
                taskAssigneeId = null,
                updateTaskTitle,
                updateTaskAssigneeId
            }
        },
        companyStore: { currCompany },
        userStore: { currUser },
        workspaceService
    } = useRootStore();
    const [workspaceMembers, setWorkspaceMembers] = useState(null);

    const fetchWorkspaceMembers = useCallback(async () => {
        try {
            const workspaceMembersResponse =
                await workspaceService.fetchWorkspaceMembers(
                    selectedAutomation.workspace_id
                );
            setWorkspaceMembers(
                workspaceMembersResponse.members.filter(
                    member => member.company?._id === currCompany._id
                )
            );
        } catch (e) {
            logError('Error fetching workspace members', e);
        }
    }, [workspaceService, currCompany, selectedAutomation.workspace_id]);

    useEffect(() => {
        if (!selectedAutomation.workspace_id) {
            return;
        }

        fetchWorkspaceMembers();
    }, [selectedAutomation, fetchWorkspaceMembers]);

    const hasError = isShowErrors && !taskTitle;
    const { t } = useTranslation(NAMESPACES.AUTOMATIONS);
    const { t: tCompanySettings } = useTranslation(
        NAMESPACES.COMPANY_SETTINGS,
        {
            keyPrefix: 'team.team_members'
        }
    );
    const { t: tTeamRoles } = useTranslation(NAMESPACES.TEAM_ROLES);

    const taskInputStyles = ((): CSSProperties => ({
        // styled-components will forward props to the DOM as attributes if
        // handled as a StyledComponent, e.g. styled(HBInput)< hasError: boolean >
        // This is a work around to prevent console errors.
        paddingRight: isCreate ? '20px' : '30px',
        backgroundColor: hasError ? `${colors.red50}1a` : colors.neutral100
    }))();

    const companyHasTeamMembers = useMemo(() => {
        return currCompany.team_members.length > 0;
    }, [currCompany.team_members]);

    const shouldAddUser = useCallback(
        (userId: string) => {
            if (!selectedAutomation.workspace_id) {
                return true;
            }

            return (workspaceMembers || []).some(
                member => member._id === userId
            );
        },
        [selectedAutomation.workspace_id, workspaceMembers]
    );

    const teamMembers = useMemo(
        () => [
            ...(shouldAddUser(currCompany.owner?._id)
                ? [currCompany.owner]
                : []),
            ...(currUser._id !== currCompany.owner?._id &&
            shouldAddUser(currUser._id)
                ? [currUser]
                : []), // Is it me in a workspace or not an instance
            ...(workspaceMembers || currCompany.team_members).filter(
                teamMember =>
                    teamMember._id !== currUser._id &&
                    teamMember._id !== currCompany.owner?._id
            )
        ],
        [currCompany, workspaceMembers, currUser, shouldAddUser]
    );

    const getTeamMemberName = useCallback(
        (_id: string, fullName: string) => {
            if (_id === currUser._id) {
                return t('sidebar.task.me', { full_name: fullName });
            }

            return fullName;
        },
        [t, currUser._id]
    );

    const teamMembersOptions = useMemo(
        () => [
            {
                label: t('sidebar.task.project_owner'),
                value: null,
                data: {
                    initials: t('sidebar.task.project_owner_initials')
                }
            },
            ...teamMembers.map(teamMember => {
                const teamMemberRole: TeamRole =
                    currCompany.userPermissionsByTeamMemberId[teamMember._id];

                const role =
                    currCompany.owner._id === teamMember._id
                        ? tCompanySettings('owner_pill')
                        : tTeamRoles(
                              `roles_selection.${teamMemberRole}.role_name`
                          );
                return {
                    label: getTeamMemberName(
                        teamMember._id,
                        teamMember.full_name
                    ),
                    value: teamMember._id,
                    data: {
                        imageUrl: teamMember.profile_image?.url,
                        role,
                        initials: teamMember.initials
                    }
                };
            })
        ],
        [
            t,
            tCompanySettings,
            tTeamRoles,
            getTeamMemberName,
            teamMembers,
            currCompany.owner._id,
            currCompany.userPermissionsByTeamMemberId
        ]
    );

    const assignTaskTo = useCallback(
        (assigneeId: string) => {
            updateTaskAssigneeId(assigneeId);

            track(AnalyticsEvents.TaskAssignTaskTo, {
                action: 'click',
                assign_user_id: assigneeId,
                assigned_to_owner: assigneeId === null,
                selection:
                    assigneeId === null
                        ? 'project owner'
                        : assigneeId === currUser._id
                        ? 'me'
                        : 'team member'
            });
        },
        [track, updateTaskAssigneeId, currUser]
    );

    return (
        <CreateTaskInputContainer>
            <TaskLabelWrapper>
                <TaskLabel hasError={hasError} type="Body6DarkPrimary">
                    {t('sidebar.task.title')}
                </TaskLabel>
                <TaskLabelCount type="Body6DarkTertiary">
                    {MAX_LENGTH - taskTitle?.length}
                </TaskLabelCount>
            </TaskLabelWrapper>
            <HBTooltip
                {...tooltipCommonProps}
                target={
                    <InputContainer>
                        <TaskInput
                            autoFocus={!taskTitle}
                            style={taskInputStyles}
                            value={taskTitle}
                            onChange={updateTaskTitle}
                            maxLength={MAX_LENGTH}
                            size="sm"
                            placeholder={t(
                                'sidebar.task.task_description_placeholder'
                            )}
                            disabled={isSelectedStepEditingDisabled}
                            testIdName="create-task"
                        />
                        {!isCreate && (
                            <StyledPencil
                                color={
                                    isSelectedStepEditingDisabled
                                        ? colors.neutral400
                                        : colors.neutral800
                                }
                            />
                        )}
                    </InputContainer>
                }
            >
                {EDIT_AUTOMATION_TOOLTIP}
            </HBTooltip>
            {companyHasTeamMembers && (
                <>
                    <AssignTaskTo>
                        <AssignToTitle>
                            {t('sidebar.task.assign_to')}
                        </AssignToTitle>
                        <HBTooltip
                            {...tooltipCommonProps}
                            forceClose={false}
                            placement="top"
                            target={<CenteredInfoCircle16 />}
                        >
                            {t('sidebar.task.assigned_to_team_member_tooltip')}
                        </HBTooltip>
                    </AssignTaskTo>
                    <HBDropdown
                        options={teamMembersOptions}
                        value={taskAssigneeId}
                        onChange={assignTaskTo}
                        size="sm"
                        disabled={isSelectedStepEditingDisabled}
                        noIndicator
                        testIdName="step-actions"
                        customOptionComponent={TeamMemberOption}
                    />
                </>
            )}
        </CreateTaskInputContainer>
    );
};

const AssignToTitle = styled.div``;

const StyledPencil = styled(Pencil16)`
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
`;

const TaskInput = styled(HBInput)`
    ${truncatedText}
`;

const CreateTaskInputContainer = styled.div`
    padding: ${spacing[12]} 0;

    &:last-of-type {
        padding-bottom: ${spacing[4]};
    }
`;
const TaskLabelWrapper = styled.div`
    display: flex;
    margin-bottom: 0.5em;
`;
const TaskLabel = styled(HBText)<{ hasError: boolean }>`
    flex: 1;
    ${is('hasError')`
        color: ${colors.red50};
    `}
`;
const TaskLabelCount = styled(HBText)`
    flex: 0;
`;

const AssignTaskTo = styled.div`
    margin-top: 32px;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CenteredInfoCircle16 = styled(InfoCircle16)`
    display: flex;
`;

const TeamMemberOption = ({ children, isSelected, index, options, data }) => {
    const { data: optionData } = data;
    return (
        <StyledOption isLastChild={index === options.length - 1}>
            <HBAvatar
                diameterInPixels={32}
                profileImageUrl={optionData?.imageUrl}
                userInitials={optionData?.initials}
            />
            <OptionContent>
                <MemberName isSelected={isSelected} type="Body6DarkPrimary">
                    {children}
                </MemberName>
                <HBText type="Body-Secondary-Extra-small">
                    {optionData?.role}
                </HBText>
            </OptionContent>
            {isSelected && <Check16 color={colors.blue20} />}
        </StyledOption>
    );
};

const MemberName = styled(HBText)<{ isSelected: boolean }>`
    ${props =>
        props.isSelected &&
        css`
            font-weight: bold;
        `}
`;

const OptionContent = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const StyledOption = styled.div<{ isLastChild: boolean }>`
    height: 51px;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 12px;

    ${props =>
        !props.isLastChild &&
        css`
            border-bottom: 1px solid ${colors.neutral200};
        `}

    &:hover {
        background-color: ${colors.neutral100};
    }
`;

export default observer(CreateTaskInput);
